import { QueryCombinerType } from 'module/api/filter/types';
import { useDialogHostedMenuPortal } from 'module/hooks/use-dialog-hosted-overlays';
import { useTranslation } from 'module/hooks/use-translation';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { Short01 } from 'module/ui/components/styles';
import { Option as SelectOption, Select } from '@rocketlane/ui';
import { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

export interface Props {
  visible?: boolean;
  value: QueryCombinerType;
  readonly?: boolean;
  onChange?: (value: QueryCombinerType) => void;
}

interface CombinerOption extends SelectOption {
  value: QueryCombinerType;
}

const Label = styled.div`
  ${Short01};
  opacity: 0.5;
`;
const Wrapper = styled(CenterAlignedBox)`
  display: inline-flex;
  height: 32px;
  ${Label} {
    padding: 0 var(--spacing-03) 0 0;
  }
`;

export const CombinerTypeSelect: FC<Props> = ({
  value,
  readonly,
  onChange,
  visible,
}) => {
  const { ref, menuPortalTarget } = useDialogHostedMenuPortal<HTMLDivElement>();
  const allLabel = useTranslation('filters.combiner.all');
  const anyLabel = useTranslation('filters.combiner.any');
  const options = useMemo<CombinerOption[]>(
    () => [
      { label: allLabel, value: QueryCombinerType.All },
      { label: anyLabel, value: QueryCombinerType.Any },
    ],
    [allLabel, anyLabel],
  );
  const selectedOption = useMemo(() => {
    return options.find((option) => option.value === value);
  }, [options, value]);
  const onCombinerTypeChange = useCallback(
    (option: CombinerOption | null) => {
      if (option) {
        onChange?.(option.value);
      }
    },
    [onChange],
  );

  if (visible === false) {
    return null;
  }

  return (
    <Wrapper ref={ref}>
      <Label>
        <FormattedMessage id="filters.combiner.title" />
      </Label>
      <Select<CombinerOption>
        className="min-w-[72px]"
        isDisabled={readonly}
        menuPortalTarget={menuPortalTarget}
        value={selectedOption}
        options={options}
        onChange={onCombinerTypeChange}
        isSearchable={false}
      />
    </Wrapper>
  );
};
